import axios from 'axios';
import { getApiUrl } from '../../config/apiConfig';
import { clearAuthSessionStorage } from '../../utils/clearSessionStorage';
import { getAuthItem, setAuthItem } from '../../utils/authStorage';
import { getCtxHomeUrl, getCtxPath, getStaffLoginUrl } from '../../config/ctxPath';

const API_HOST = getApiUrl();
const CTX_PATH = getCtxPath();

const axiosInstance = axios.create({
    baseURL: API_HOST,
    timeout: 20000,
    headers: {
        'Content-Type': "application/json"
    }
});

const isPublicAuthUrl = (url) => {
    if (!url) return false;
    return (
        url.includes('/signin') ||
        url.includes('/portal/resolve') ||
        url.includes('/forgot-password') ||
        url.includes('/reset-password') ||
        url.includes('/client/request-otp') ||
        url.includes('/client/verify-otp') ||
        url.includes('/refresh')
    );
};

/** API rejected a token from before the unified login (or for a removed user). */
const isOutdatedTokenResponse = (response) =>
    response?.status === 401 && response?.data?.code === 'token_outdated';

const isRefreshUrl = (url) => {
    if (!url) return false;
    return url.includes('/refresh');
};

function clearSessionAndRedirect() {
    const accountType = getAuthItem('account_type');
    clearAuthSessionStorage();

    const currentPath = window.location.pathname;
    const home = getCtxHomeUrl();
    const staffLogin = getStaffLoginUrl();
    const forgot = CTX_PATH ? `${CTX_PATH}/forgot-password` : '/forgot-password';
    const reset = CTX_PATH ? `${CTX_PATH}/reset-password` : '/reset-password';
    const redirectTo = accountType === 'client' ? home : staffLogin;

    const onAuthPage =
        currentPath === home ||
        currentPath === (CTX_PATH || '/') ||
        currentPath === staffLogin ||
        currentPath === '/login' ||
        (CTX_PATH && currentPath === `${CTX_PATH}/login`) ||
        currentPath === forgot ||
        currentPath === '/forgot-password' ||
        currentPath === reset ||
        currentPath === '/reset-password';

    if (!onAuthPage) {
        window.location.href = redirectTo;
    }
}

let isRefreshing = false;
let refreshQueue = [];

function processRefreshQueue(error, token = null) {
    refreshQueue.forEach(({ resolve, reject }) => {
        if (error) reject(error);
        else resolve(token);
    });
    refreshQueue = [];
}

async function refreshAccessToken() {
    const refreshToken = getAuthItem('refresh_token');
    if (!refreshToken) {
        throw new Error('No refresh token available.');
    }

    const response = await axios.post(
        `${API_HOST}/refresh`,
        {},
        {
            headers: {
                Authorization: `Bearer ${refreshToken}`,
                'Content-Type': 'application/json',
            },
            timeout: 20000,
        }
    );

    const newAccessToken = response.data?.data?.access_token;
    if (!newAccessToken) {
        throw new Error('Refresh response did not include an access token.');
    }

    setAuthItem('access_token', newAccessToken);
    return newAccessToken;
}

axiosInstance.interceptors.request.use(
    (config) => {
        const isAuthEndpoint = isPublicAuthUrl(config.url);

        if (!isAuthEndpoint) {
            const token = getAuthItem('access_token');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }

        const data = config.data;
        const isFormData =
            typeof FormData !== "undefined" &&
            ((data instanceof FormData) ||
              Object.prototype.toString.call(data) === "[object FormData]");

        if (isFormData) {
            config.headers = config.headers || {};
            delete config.headers["Content-Type"];
            delete config.headers["content-type"];
        }

        return config;
    },
    (error) => {
        console.error("Request interceptor error:", error);
        return Promise.reject(error);
    }
);

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response) {
            console.error("API Error Response:", {
                url: originalRequest?.url,
                method: originalRequest?.method,
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data,
                headers: error.response.headers
            });

            const isAuthEndpoint = isPublicAuthUrl(originalRequest?.url);

            if (!isAuthEndpoint && isOutdatedTokenResponse(error.response)) {
                clearSessionAndRedirect();
                return Promise.reject(error);
            }

            if (
                error.response.status === 401 &&
                !isAuthEndpoint &&
                !originalRequest?._retry &&
                !isRefreshUrl(originalRequest?.url)
            ) {
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        refreshQueue.push({ resolve, reject });
                    }).then((token) => {
                        originalRequest.headers = originalRequest.headers || {};
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return axiosInstance(originalRequest);
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    const newAccessToken = await refreshAccessToken();
                    processRefreshQueue(null, newAccessToken);
                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    return axiosInstance(originalRequest);
                } catch (refreshError) {
                    processRefreshQueue(refreshError, null);
                    clearSessionAndRedirect();
                    console.error("Authentication error:", refreshError.message || refreshError);
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            }
        } else if (error.request) {
            console.error("API Request Error (no response):", {
                url: originalRequest?.url,
                method: originalRequest?.method,
                message: error.message
            });
        } else {
            console.error("API Error:", error.message);
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
